/**
 * WP-B — The product's only access path to conversations and messages.
 *
 * Cutover contract, enforced structurally by this module:
 *
 *   * every conversation, message and proposal the product renders comes from a
 *     hosted authority read;
 *   * every write goes through an authenticated server command; nothing is
 *     written to localStorage and nothing is invented locally;
 *   * after a successful turn the affected queries are INVALIDATED and re-read,
 *     so the screen shows what the backend stored rather than what the browser
 *     hoped for. Ids, ordering and timestamps are never predicted here;
 *   * failures surface as categorical, pre-written sentences. No provider
 *     message, SQL text, identifier or raw exception can reach the UI.
 *
 * React Query is a transient view cache only.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useMemo, useState } from "react";

import type { ChatFailureReason } from "../chatErrors";
import {
  createChatConversation,
  listChatConversations,
  loadChatTranscript,
  submitChatTurn,
} from "../chat.functions";
import {
  canonicalizeHostedProposal,
  editHostedProposal,
} from "@/lib/loops/canonicalization/canonicalization.functions";
import type { CanonicalizationFailureReason } from "@/lib/loops/canonicalization/canonicalizationErrors";
import type { ProposalEdits } from "@/lib/loops/canonicalization/canonicalizationRequest";
import type { Conversation, LoopPreviewDraft, Message } from "@/lib/looseend/types";

const CONVERSATIONS_KEY = ["hostedConversations"] as const;
const transcriptKey = (id: string) => ["hostedTranscript", id] as const;
/** The Loop surface's list key: a canonical Loop must appear there after re-read. */
const LOOPS_KEY = ["hostedLoops"] as const;

export function chatFailureMessage(reason: ChatFailureReason): string {
  switch (reason) {
    case "validation_failed":
      return "That message can't be sent as written.";
    case "not_found_or_not_authorized":
      return "This conversation is no longer available.";
    case "not_authorized":
    case "account_not_active":
    case "workspace_not_active":
      return "Your account can't use conversations right now.";
    case "loop_not_found_or_not_authorized":
      return "That Loop is no longer available.";
    case "conversation_kind_mismatch":
      return "This conversation can't be opened.";
    case "transient_conflict":
      return "Another change landed first. Try again.";
    default:
      return "Conversations are briefly unavailable.";
  }
}

/** Categorical sentences for the canonicalization vocabulary (WP-C1 §14). */
export function canonicalizationFailureMessage(reason: CanonicalizationFailureReason): string {
  switch (reason) {
    case "validation_failed":
      return "This suggestion can't be saved as written. Check the details and try again.";
    case "stale_proposal":
      return "This suggestion changed since you last looked. Review it again before saving.";
    case "proposal_not_confirmable":
      return "This suggestion is no longer available to save.";
    case "proposal_not_editable":
      return "This suggestion can no longer be edited.";
    case "not_found_or_not_authorized":
      return "This suggestion is no longer available.";
    case "role_not_permitted":
      return "Sign in to keep track of this.";
    case "account_not_active":
    case "workspace_not_active":
      return "Your account can't save Loops right now.";
    case "idempotency_conflict":
      return "This save couldn't be completed. Refresh and try again.";
    case "transient_conflict":
      return "Another change landed first. Try again.";
    default:
      return "Loops are briefly unavailable.";
  }
}

export type ChatPhase = "loading" | "ready" | "not_found" | "unavailable";

export interface HostedConversationsView {
  readonly phase: "loading" | "ready" | "unavailable";
  readonly conversations: readonly Conversation[];
  readonly message: string | null;
  readonly refresh: () => void;
}

export function useHostedConversations(): HostedConversationsView {
  const list = useServerFn(listChatConversations);
  const client = useQueryClient();
  const query = useQuery({
    queryKey: CONVERSATIONS_KEY,
    queryFn: () => list({}),
    staleTime: 10_000,
    retry: 1,
  });

  const refresh = useCallback(() => {
    void client.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
  }, [client]);

  return useMemo<HostedConversationsView>(() => {
    if (query.isPending) return { phase: "loading", conversations: [], message: null, refresh };
    const outcome = query.data;
    if (query.isError || outcome === undefined || !outcome.ok) {
      const reason: ChatFailureReason = outcome && !outcome.ok ? outcome.reason : "unavailable";
      return {
        phase: "unavailable",
        conversations: [],
        message: chatFailureMessage(reason),
        refresh,
      };
    }
    return {
      phase: "ready",
      conversations: outcome.value.map((row) => ({
        id: row.id,
        title: row.title,
        createdAt: row.createdAt,
        updatedAt: row.updatedAt,
        archivedAt: row.archivedAt,
      })),
      message: null,
      refresh,
    };
  }, [query.isPending, query.isError, query.data, refresh]);
}

export interface HostedTranscriptView {
  readonly phase: ChatPhase;
  readonly title: string | null;
  readonly messages: readonly Message[];
  readonly message: string | null;
  readonly refresh: () => void;
}

/**
 * The durable transcript, projected into the view shape the message list already
 * renders. Proposals are attached to the assistant message of the turn they were
 * derived from; a proposal already confirmed carries the canonical Loop id taken
 * from the authoritative reference, never from local memory.
 */
export function useHostedTranscript(conversationId: string): HostedTranscriptView {
  const load = useServerFn(loadChatTranscript);
  const client = useQueryClient();
  const query = useQuery({
    queryKey: transcriptKey(conversationId),
    queryFn: () => load({ data: { conversationId } }),
    staleTime: 5_000,
    retry: 1,
  });

  const refresh = useCallback(() => {
    void client.invalidateQueries({ queryKey: transcriptKey(conversationId) });
  }, [client, conversationId]);

  return useMemo<HostedTranscriptView>(() => {
    if (query.isPending)
      return { phase: "loading", title: null, messages: [], message: null, refresh };
    const outcome = query.data;
    if (query.isError || outcome === undefined) {
      return {
        phase: "unavailable",
        title: null,
        messages: [],
        message: chatFailureMessage("unavailable"),
        refresh,
      };
    }
    if (!outcome.ok) {
      const notFound =
        outcome.reason === "not_found_or_not_authorized" ||
        outcome.reason === "conversation_kind_mismatch";
      return {
        phase: notFound ? "not_found" : "unavailable",
        title: null,
        messages: [],
        message: chatFailureMessage(outcome.reason),
        refresh,
      };
    }

    const { conversation, messages, proposals, references, research } = outcome.value;
    const ordered = [...messages];
    const researchByMessage = new Map(research.map((r) => [r.messageId, r]));
    const proposalBySourceMessage = new Map(proposals.map((p) => [p.sourceMessageId, p]));
    const loopByMessage = new Map(
      references.filter((r) => r.messageId !== null).map((r) => [r.messageId as string, r.loopId]),
    );

    const view: Message[] = ordered.map((row, index) => {
      // A proposal belongs to the assistant reply of the turn whose user message
      // produced it: the message immediately before this one, in server order.
      const previous = index > 0 ? ordered[index - 1] : undefined;
      const proposal =
        row.role === "assistant" && previous ? proposalBySourceMessage.get(previous.id) : undefined;
      // The canonical Loop is read from the authoritative conversation reference.
      // WP-C1 records it against the proposal's SOURCE message; the pre-C1 path
      // recorded it against the assistant message. Both are hosted facts.
      const savedLoopId =
        loopByMessage.get(row.id) ??
        (proposal ? (loopByMessage.get(proposal.sourceMessageId) ?? null) : null);

      return {
        id: row.id,
        conversationId: row.conversationId,
        role: row.role,
        content: row.content,
        createdAt: row.createdAt,
        ...(proposal
          ? {
              loopPreview: proposal.draft,
              proposal: {
                id: proposal.id,
                status: proposal.status,
                recordVersion: proposal.recordVersion,
              },
            }
          : {}),
        savedLoopId,
        research: researchByMessage.has(row.id)
          ? {
              query: researchByMessage.get(row.id)!.query,
              retrievedAt: researchByMessage.get(row.id)!.retrievedAt,
              state: researchByMessage.get(row.id)!.state,
              sources: researchByMessage.get(row.id)!.sources.map((s) => ({ ...s })),
            }
          : null,
      };
    });

    return {
      phase: "ready",
      title: conversation.title,
      messages: view,
      message: null,
      refresh,
    };
  }, [query.isPending, query.isError, query.data, refresh]);
}

export type ChatMutationResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly message: string; readonly retryable: boolean };

export interface SaveProposalInput {
  readonly conversationId: string;
  /** Durable proposal identity and the version the user reviewed. */
  readonly proposalId: string;
  readonly expectedVersion: number;
  /** What the hosted read showed the user. */
  readonly stored: LoopPreviewDraft;
  /** What the user is saving (possibly edited in the card). */
  readonly reviewed: LoopPreviewDraft;
}

export interface SavedProposalResult {
  readonly loopId: string;
  /** `EXISTING` means a legitimate retry: the Loop was created once, earlier. */
  readonly disposition: "CREATED" | "EXISTING";
}

export interface HostedChatActions {
  readonly busy: boolean;
  readonly startConversation: (title?: string) => Promise<ChatMutationResult<string>>;
  readonly sendTurn: (
    conversationId: string,
    content: string,
  ) => Promise<ChatMutationResult<{ readonly assistantMessageId: string }>>;
  /**
   * WP-C1 — Save a proposal as a Loop through the canonicalization authority.
   * Edits are persisted to the DURABLE proposal first (a new version), then the
   * durable row — never the browser's draft — is canonicalized.
   */
  readonly saveProposal: (
    input: SaveProposalInput,
  ) => Promise<ChatMutationResult<SavedProposalResult>>;
}

/**
 * Only fields the user actually changed are sent; the authority owns the rest.
 * Returns an empty object when the reviewed draft equals the stored one.
 */
export function proposalEditsBetween(
  stored: LoopPreviewDraft,
  reviewed: LoopPreviewDraft,
): ProposalEdits {
  const edits: Record<string, string | null> = {};
  const norm = (value: string | null | undefined) => (value ?? "").trim();
  if (norm(stored.title) !== norm(reviewed.title)) edits["title"] = reviewed.title.trim();
  if (norm(stored.objective) !== norm(reviewed.objective))
    edits["objective"] = reviewed.objective.trim();
  if (norm(stored.counterparty) !== norm(reviewed.counterparty)) {
    edits["counterparty"] = norm(reviewed.counterparty) === "" ? null : norm(reviewed.counterparty);
  }
  if (stored.status !== reviewed.status) edits["proposedLoopStatus"] = reviewed.status;
  if (norm(stored.expectedEvent) !== norm(reviewed.expectedEvent)) {
    edits["expectedEvent"] = reviewed.expectedEvent.trim();
  }
  const storedDate = stored.expectedDate ? stored.expectedDate.slice(0, 10) : null;
  const reviewedDate = reviewed.expectedDate ? reviewed.expectedDate.slice(0, 10) : null;
  if (storedDate !== reviewedDate) edits["expectedDate"] = reviewedDate;
  if (norm(stored.nextAction) !== norm(reviewed.nextAction))
    edits["nextAction"] = reviewed.nextAction.trim();
  if (norm(stored.triggerCondition) !== norm(reviewed.triggerCondition)) {
    edits["triggerCondition"] = reviewed.triggerCondition.trim();
  }
  if (norm(stored.resolutionCondition) !== norm(reviewed.resolutionCondition)) {
    edits["resolutionCondition"] = reviewed.resolutionCondition.trim();
  }
  return edits as ProposalEdits;
}

export function useHostedChatActions(): HostedChatActions {
  const create = useServerFn(createChatConversation);
  const turn = useServerFn(submitChatTurn);
  const edit = useServerFn(editHostedProposal);
  const canonicalize = useServerFn(canonicalizeHostedProposal);
  const client = useQueryClient();
  const [busy, setBusy] = useState(false);

  const startConversation = useCallback(
    async (title?: string): Promise<ChatMutationResult<string>> => {
      setBusy(true);
      try {
        const outcome = await create({ data: title === undefined ? {} : { title } });
        if (!outcome.ok)
          return {
            ok: false,
            message: chatFailureMessage(outcome.reason),
            retryable: outcome.retryable,
          };
        await client.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
        return { ok: true, value: outcome.value.id };
      } catch {
        return { ok: false, message: chatFailureMessage("unavailable"), retryable: true };
      } finally {
        setBusy(false);
      }
    },
    [create, client],
  );

  const sendTurn = useCallback(
    async (
      conversationId: string,
      content: string,
    ): Promise<ChatMutationResult<{ readonly assistantMessageId: string }>> => {
      setBusy(true);
      try {
        const outcome = await turn({ data: { conversationId, content } });
        if (!outcome.ok)
          return {
            ok: false,
            message: chatFailureMessage(outcome.reason),
            retryable: outcome.retryable,
          };
        await client.invalidateQueries({ queryKey: transcriptKey(conversationId) });
        await client.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
        return { ok: true, value: { assistantMessageId: outcome.value.assistantMessage.id } };
      } catch {
        return { ok: false, message: chatFailureMessage("unavailable"), retryable: true };
      } finally {
        setBusy(false);
      }
    },
    [turn, client],
  );

  /**
   * WP-C1 — Confirmation IS canonicalization.
   *
   * 1. If the user edited the card, the edits are persisted to the durable
   *    proposal first; the authority returns the new version. A stale edit is a
   *    categorical refusal and nothing is created.
   * 2. The durable proposal (by id + version) is canonicalized. The backend
   *    creates the Loop, confirms the proposal and records the conversation
   *    lineage in ONE transaction, so there is no separate lineage step to lose.
   * 3. The transcript and Loop views are invalidated and re-read: what appears
   *    is what the backend stored. Nothing is marked saved locally.
   */
  const saveProposal = useCallback<HostedChatActions["saveProposal"]>(
    async ({ conversationId, proposalId, expectedVersion, stored, reviewed }) => {
      setBusy(true);
      try {
        let version = expectedVersion;
        const edits = proposalEditsBetween(stored, reviewed);
        if (Object.keys(edits).length > 0) {
          const edited = await edit({ data: { proposalId, expectedVersion: version, edits } });
          if (!edited.ok) {
            return {
              ok: false,
              message: canonicalizationFailureMessage(edited.reason),
              retryable: edited.retryable,
            };
          }
          version = edited.record.recordVersion;
        }

        const outcome = await canonicalize({ data: { proposalId, expectedVersion: version } });
        if (!outcome.ok) {
          return {
            ok: false,
            message: canonicalizationFailureMessage(outcome.reason),
            retryable: outcome.retryable,
          };
        }
        return {
          ok: true,
          value: { loopId: outcome.record.loopId, disposition: outcome.record.disposition },
        };
      } catch {
        return {
          ok: false,
          message: canonicalizationFailureMessage("unavailable"),
          retryable: true,
        };
      } finally {
        await client.invalidateQueries({ queryKey: transcriptKey(conversationId) });
        await client.invalidateQueries({ queryKey: LOOPS_KEY });
        setBusy(false);
      }
    },
    [edit, canonicalize, client],
  );

  return { busy, startConversation, sendTurn, saveProposal };
}
